<template>
  <div class="stp">
    <div class="stp-header">
      <h2 class="stp-title">Start from a Template</h2>
      <p class="stp-subtitle">Pre-written SMS messages you can customize</p>
    </div>

    <div class="stp-filter-row">
      <label class="stp-filter-label">Filter by:</label>
      <select v-model="activeFilter" class="stp-filter-select">
        <option value="all">All Templates</option>
        <option v-for="tag in filterTags" :key="tag" :value="tag">{{ tag }}</option>
      </select>
    </div>

    <div class="stp-grid">
      <div
        v-for="tpl in filteredTemplates"
        :key="tpl.id"
        class="stp-card"
        @click="$emit('select-template', tpl)"
      >
        <div class="stp-preview">
          <i class="material-icons stp-preview-icon">textsms</i>
          <div class="stp-preview-text">{{ tpl.body }}</div>
        </div>
        <div class="stp-card-info">
          <div class="stp-card-name">{{ tpl.name }}</div>
          <div class="stp-card-desc">{{ tpl.description }}</div>
          <div class="stp-card-tags">
            <span v-for="tag in tpl.tags" :key="tag" class="stp-tag" :class="tagClass(tag)">{{ tag }}</span>
          </div>
        </div>
      </div>
    </div>

    <div class="stp-header stp-header--blank">
      <h2 class="stp-title">Or Start from Scratch</h2>
    </div>
    <div class="stp-grid">
      <div class="stp-card stp-card--blank" @click="$emit('select-blank')">
        <div class="stp-preview stp-preview--blank">
          <i class="material-icons stp-preview-icon">add</i>
        </div>
        <div class="stp-card-info">
          <div class="stp-card-name">Blank Template</div>
          <div class="stp-card-desc">Write your own message from scratch.</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { SMS_TEMPLATES, SMS_TEMPLATE_FILTER_TAGS } from '../../helpers/sms-templates';

const TAG_CLASSES = {
  Lead: 'stp-tag--lead',
  Student: 'stp-tag--student',
  Employer: 'stp-tag--employer',
  General: 'stp-tag--general',
};

export default {
  name: 'SMSTemplatePicker',
  data() {
    return {
      templates: SMS_TEMPLATES,
      activeFilter: 'all',
    };
  },
  computed: {
    filterTags() {
      return SMS_TEMPLATE_FILTER_TAGS;
    },
    filteredTemplates() {
      if (this.activeFilter === 'all') return this.templates;
      const filter = this.activeFilter;
      return this.templates.filter(t => t.tags && t.tags.indexOf(filter) !== -1);
    },
  },
  methods: {
    tagClass(tag) {
      return TAG_CLASSES[tag] || '';
    },
  },
};
</script>

<style scoped>
.stp {
  max-width: 960px;
  margin: 0 auto;
  padding: 24px 0;
}
.stp-header { text-align: center; margin-bottom: 20px; }
.stp-header--blank {
  margin-top: 40px;
  padding-top: 32px;
  border-top: 1px solid #e2e6ea;
}
.stp-title {
  font-size: 22px;
  font-weight: 700;
  color: #181818;
  margin: 0 0 6px 0;
}
.stp-subtitle {
  font-size: 14px;
  color: #5f6368;
  margin: 0;
}

.stp-filter-row {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-bottom: 24px;
}
.stp-filter-label {
  font-size: 14px;
  font-weight: 600;
  color: #5f6368;
}
.stp-filter-select {
  height: 38px;
  min-width: 180px;
  padding: 0 32px 0 12px;
  border: 2px solid #dadce0;
  border-radius: 8px;
  background: #fff;
  font-size: 14px;
  font-weight: 500;
  color: #181818;
  cursor: pointer;
  outline: none;
  transition: border-color 0.15s;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6'%3E%3Cpath d='M0 0l5 6 5-6z' fill='%235f6368'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 10px center;
  background-size: 10px 6px;
}
.stp-filter-select:focus {
  border-color: var(--primary-color, #3b86f5);
}

.stp-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 16px;
}

.stp-card {
  border: 2px solid #e2e6ea;
  border-radius: 10px;
  background: #fff;
  cursor: pointer;
  transition: border-color 0.15s, box-shadow 0.15s, transform 0.15s;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}
.stp-card:hover {
  border-color: var(--primary-color, #3b86f5);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  transform: translateY(-2px);
}

.stp-preview {
  background: #f4f3ed;
  padding: 14px 16px;
  min-height: 96px;
  border-bottom: 1px solid #e2e6ea;
  display: flex;
  gap: 10px;
  align-items: flex-start;
}
.stp-preview--blank {
  justify-content: center;
  align-items: center;
}
.stp-preview-icon {
  font-size: 22px;
  color: var(--primary-color, #3b86f5);
  opacity: 0.7;
  flex-shrink: 0;
}
.stp-preview-text {
  font-size: 12px;
  color: #5f6368;
  line-height: 1.45;
  display: -webkit-box;
  -webkit-line-clamp: 4;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.stp-card-info { padding: 12px 14px; }
.stp-card-name {
  font-size: 15px;
  font-weight: 600;
  color: #181818;
  margin-bottom: 4px;
}
.stp-card-desc {
  font-size: 12px;
  color: #5f6368;
  line-height: 1.4;
  margin-bottom: 8px;
}
.stp-card-tags {
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
}
.stp-tag {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 500;
  background: #f4f3ed;
  color: #5f6368;
}
.stp-tag--lead { background: #e8f0fe; color: #1967d2; }
.stp-tag--student { background: #e6f4ea; color: #137333; }
.stp-tag--employer { background: #fef7e0; color: #b06000; }
.stp-tag--general { background: #f4f3ed; color: #5f6368; }
</style>
